/**
 * Orbit-follow camera.
 *
 * Three things make or break a camera in a game where you are airborne almost
 * all the time:
 *
 * 1. **Split damping.** The focus point chases the player fast in XZ (τ = 0.08 s)
 *    but slowly in Y (τ = 0.25 s). Idle pogo bouncing is a ~1 m sine wave at a
 *    couple of hertz; a camera that tracks it vertically is nauseating within
 *    seconds.
 * 2. **Fall bias.** Past −12 m/s the focus slides downward, up to 24 m, so a long
 *    fall shows you what you are about to hit instead of the sky you just left.
 * 3. **Occlusion.** The course wraps a shaft, so the geometry you just jumped off
 *    is constantly between you and the camera. A sphere sweep pulls the camera in
 *    on contact and lets it drift back out slowly.
 *
 * The camera's `yaw` is also the game's input frame — `pogo.ts` maps WASD through
 * it — so it is a plain public field, readable at any time.
 */

import type { PerspectiveCamera, Vector3 as V3Type } from 'three';
import { Vector3 } from 'three';
import type { Broadphase } from '../physics/broadphase';
import { sweepSphere } from '../physics/collision';

const DEG = Math.PI / 180;

/** Mouse sensitivity, radians per pixel, before the user's scale is applied. */
const YAW_SENS = 0.0022;
const PITCH_SENS = 0.0018;
/** You spend this game looking almost straight up and almost straight down. */
const PITCH_MIN = -72 * DEG;
const PITCH_MAX = 78 * DEG;

/** Wheel distance range and default. */
const DIST_MIN = 4;
const DIST_MAX = 12;
const DIST_DEFAULT = 6.5;
/** Metres of extra pull-back per unit of wheel delta (one notch ≈ 100). */
const WHEEL_SCALE = 0.006;

/** Focus damping time constants, seconds. */
const TAU_XZ = 0.08;
const TAU_Y = 0.25;
/** Height above the player's origin that the camera actually looks at. */
const FOCUS_LIFT = 1;

/** Fall bias: engages below this vertical speed, blends with this time constant. */
const FALL_VY = -12;
const FALL_GAIN = 0.35;
const FALL_MAX = 24;
const TAU_FALL = 0.4;

/** Distance auto-extension with speed. */
const SPEED_DIST_REF = 30;
const SPEED_DIST_EXTRA = 3;

/** FOV widening with speed. */
const FOV_BASE = 70;
const FOV_FAST = 86;
const SPEED_FOV_REF = 35;
const FOV_RATE = 3;
const FOV_EPS = 0.05;

/** Occlusion probe. */
const PROBE_RADIUS = 0.35;
/** How far in front of a blocking surface the camera stops. */
const OCCLUSION_MARGIN = 0.3;
/** Never come closer than this — inside the skeleton is worse than clipped. */
const OCCLUSION_MIN = 1.2;
/** Metres per second the camera is allowed to drift back out after a pull-in. */
const RECOVER_RATE = 6;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Frame-rate independent damping factor for a time constant. */
function damp(dt: number, tau: number): number {
  return 1 - Math.exp(-dt / tau);
}

export class OrbitFollowCamera {
  /** Yaw around +Y, radians. Also the frame WASD lean is expressed in. */
  yaw = 0;
  /** Pitch, radians. Negative looks down at the player from above. */
  pitch = -0.18;

  private readonly cam: PerspectiveCamera;
  private readonly bp: Broadphase | null;

  /** Wheel-controlled base distance, before speed extension and occlusion. */
  private baseDistance = DIST_DEFAULT;
  /** Distance actually used this frame, after occlusion pull-in. */
  private distance = DIST_DEFAULT;
  private sensitivity = 1;

  /** Damped focus point, before the fall bias is subtracted. */
  private readonly focus = new Vector3();
  /** Smoothed downward focus offset from falling. */
  private fallBias = 0;
  private fovCurrent = FOV_BASE;
  private initialised = false;

  private readonly _look = new Vector3();
  private readonly _ideal = new Vector3();
  private readonly _offset = new Vector3();

  constructor(camera: PerspectiveCamera, bp: Broadphase | null) {
    this.cam = camera;
    this.bp = bp;
    this.fovCurrent = camera.fov;
  }

  /** Feed raw pointer-lock deltas, in pixels. */
  addMouseDelta(dx: number, dy: number): void {
    this.yaw -= dx * YAW_SENS * this.sensitivity;
    this.pitch = clamp(this.pitch - dy * PITCH_SENS * this.sensitivity, PITCH_MIN, PITCH_MAX);
  }

  /** Feed a raw wheel `deltaY`. Positive pulls the camera out. */
  addWheelDelta(d: number): void {
    this.baseDistance = clamp(this.baseDistance + d * WHEEL_SCALE, DIST_MIN, DIST_MAX);
  }

  /** Multiplier on mouse sensitivity; 1 is the default. */
  setSensitivityScale(s: number): void {
    this.sensitivity = s > 0 ? s : 1;
  }

  /** Current wheel distance, for a settings readout. */
  getBaseDistance(): number {
    return this.baseDistance;
  }

  /** Snap the focus to the player without damping — use after a respawn. */
  reset(targetPos: V3Type): void {
    this.focus.set(targetPos.x, targetPos.y + FOCUS_LIFT, targetPos.z);
    this.fallBias = 0;
    this.distance = this.baseDistance;
    this.initialised = true;
  }

  /**
   * Advance one rendered frame.
   *
   * @param dt   seconds since the last call
   * @param targetPos the player's position
   * @param targetVel the player's velocity, for the fall bias, distance and FOV
   */
  update(dt: number, targetPos: V3Type, targetVel: V3Type): void {
    if (!this.initialised) this.reset(targetPos);
    const step = dt > 0 ? dt : 0;

    // --- focus: fast in XZ, slow in Y ---------------------------------------
    const kXZ = damp(step, TAU_XZ);
    const kY = damp(step, TAU_Y);
    this.focus.x += (targetPos.x - this.focus.x) * kXZ;
    this.focus.z += (targetPos.z - this.focus.z) * kXZ;
    this.focus.y += (targetPos.y + FOCUS_LIFT - this.focus.y) * kY;

    // --- fall bias -----------------------------------------------------------
    const wantBias = targetVel.y < FALL_VY ? Math.min(FALL_MAX, -targetVel.y * FALL_GAIN) : 0;
    this.fallBias += (wantBias - this.fallBias) * damp(step, TAU_FALL);
    this._look.set(this.focus.x, this.focus.y - this.fallBias, this.focus.z);

    // --- distance and FOV from speed ----------------------------------------
    const speed = Math.hypot(targetVel.x, targetVel.y, targetVel.z);
    const wantDistance =
      this.baseDistance + SPEED_DIST_EXTRA * clamp(speed / SPEED_DIST_REF, 0, 1);

    const wantFov = FOV_BASE + (FOV_FAST - FOV_BASE) * clamp(speed / SPEED_FOV_REF, 0, 1);
    this.fovCurrent += (wantFov - this.fovCurrent) * damp(step, 1 / FOV_RATE);
    if (Math.abs(this.fovCurrent - this.cam.fov) > FOV_EPS) {
      this.cam.fov = this.fovCurrent;
      this.cam.updateProjectionMatrix();
    }

    // --- occlusion ------------------------------------------------------------
    // `_offset` is the unit direction from the focus out to the camera.
    const cp = Math.cos(this.pitch);
    this._offset.set(Math.sin(this.yaw) * cp, -Math.sin(this.pitch), Math.cos(this.yaw) * cp);

    let allowed = wantDistance;
    if (this.bp !== null) {
      this._ideal.copy(this._look).addScaledVector(this._offset, wantDistance);
      const candidates = this.bp.querySegment(this._look, this._ideal, PROBE_RADIUS + 0.05);
      let nearest = 1;
      for (const prim of candidates) {
        const hit = sweepSphere(prim, this._look, this._ideal, PROBE_RADIUS);
        if (hit !== null && hit.t < nearest) nearest = hit.t;
      }
      if (nearest < 1) {
        allowed = Math.max(OCCLUSION_MIN, nearest * wantDistance - OCCLUSION_MARGIN);
      }
    }

    // Snap inward instantly (being inside a wall is never acceptable), drift back
    // out at a fixed rate so a moment of occlusion does not yank the view.
    if (allowed < this.distance) this.distance = allowed;
    else this.distance = Math.min(allowed, this.distance + RECOVER_RATE * step);

    // --- compose --------------------------------------------------------------
    this.cam.position.copy(this._look).addScaledVector(this._offset, this.distance);
    this.cam.lookAt(this._look);
  }
}
